import React, { useState, useEffect, useCallback } from 'react';
import { Table, Input, Select, Button, Modal, Form, DatePicker, Tag, Space, message, Card, Row, Col, Divider } from 'antd';
import { SearchOutlined, PlusOutlined, EditOutlined, DeleteOutlined, PrinterOutlined } from '@ant-design/icons';
import axios from 'axios';
import dayjs from 'dayjs';
import 'dayjs/locale/ru';
import locale from 'antd/es/date-picker/locale/ru_RU';
// Устанавливаем русскую локаль для dayjs
dayjs.locale('ru');

// Компонент DatePicker с маской
const MaskedDateField = ({ value, onChange, placeholder = 'ДД.ММ.ГГГГ' }) => {
  const [inputValue, setInputValue] = useState('');
  const inputRef = React.useRef(null);

  useEffect(() => {
    if (value) {
      setInputValue(dayjs(value).format('DD.MM.YYYY'));
    } else {
      setInputValue('');
    }
  }, [value]);

  const handleChange = (e) => {
    let val = e.target.value;
    
    // Если поле пустое - отправляем null
    if (!val || val.trim() === '') {
      setInputValue('');
      onChange(null);
      return;
    }
    
    // Удаляем все нецифровые символы
    val = val.replace(/\D/g, '');
    
    // Ограничиваем до 8 цифр
    if (val.length > 8) val = val.slice(0, 8);
    
    // Форматируем с точками
    if (val.length >= 2 && val.length < 4) {
      val = val.slice(0, 2) + '.' + val.slice(2);
    } else if (val.length >= 4) {
      val = val.slice(0, 2) + '.' + val.slice(2, 4) + '.' + val.slice(4);
    }
    
    setInputValue(val);
    
    // Если дата полностью введена (10 символов с точками)
    if (val && val.length === 10) {
      const date = dayjs(val, 'DD.MM.YYYY');
      if (date.isValid()) {
        onChange(date);
      }
    }
  };

  const handleBlur = () => {
    if (!inputValue || inputValue.trim() === '') {
      setInputValue('');
      onChange(null);
      return;
    }
    if (inputValue && inputValue.length === 10) {
      const date = dayjs(inputValue, 'DD.MM.YYYY');
      if (!date.isValid()) {
        setInputValue('');
        onChange(null);
      }
    } else if (inputValue && inputValue.length < 10) {
      setInputValue('');
      onChange(null);
    }
  };

  const handleDatePickerChange = (date) => {
    onChange(date);
    if (date) {
      setInputValue(date.format('DD.MM.YYYY'));
    } else {
      setInputValue('');
      onChange(null);
    }
  };

  return (
    <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
      <Input
        ref={inputRef}
        value={inputValue}
        onChange={handleChange}
        onBlur={handleBlur}
        placeholder={placeholder}
        style={{ paddingRight: '35px' }}
      />
      <DatePicker
        locale={locale}
        value={value}
        onChange={handleDatePickerChange}
        format="DD.MM.YYYY"
        style={{ 
          position: 'absolute', 
          right: '2px',
          top: '2px',
          bottom: '2px',
          width: '32px',
          opacity: 0,
          cursor: 'pointer',
          zIndex: 1
        }}
        placeholder=""
        allowClear={false}
        suffixIcon={null}
        getPopupContainer={(trigger) => trigger.parentNode}
      />
      <span 
        style={{ 
          position: 'absolute', 
          right: '8px', 
          top: '50%', 
          transform: 'translateY(-50%)',
          pointerEvents: 'none',
          color: '#999',
          fontSize: '16px',
          zIndex: 0
        }}
      >
        📅
      </span>
    </div>
  );
};

const { Option } = Select;

const API_URL = '/api/workers';

function Workers() {
  const [workers, setWorkers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState(null);
  const [expiringFilter, setExpiringFilter] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingWorker, setEditingWorker] = useState(null);
  const [form] = Form.useForm();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [pageSize, setPageSize] = useState(30);

  // Debounce поиска — задержка 400мс
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchText);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchText]);

  const fetchWorkers = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (debouncedSearch) params.append('search', debouncedSearch);
      if (statusFilter) params.append('status', statusFilter);

      const response = await axios.get(`${API_URL}?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      let workers = response.data;

      // Фильтрация на клиенте по документам
      if (expiringFilter) {
        const now = new Date();

        workers = workers.filter(worker => {
          const docs = [
            { date: worker.patent_expiry_date, name: 'Патент' },
            { date: worker.payment_date, name: 'Оплата' },
            { date: worker.registration_date, name: 'Регистрация' },
            { date: worker.insurance_date, name: 'Мед. страховка' },
            { date: worker.medical_policy_date, name: 'Мед. полис' },
            { date: worker.exam_date, name: 'Экзамен' }
          ].filter(d => d.date);

          if (docs.length === 0) return expiringFilter === 'actual';

          const daysArray = docs.map(d => {
            const docDate = new Date(d.date);
            const diff = docDate - now;
            return Math.ceil(diff / (1000 * 60 * 60 * 24));
          });

          const hasExpired = daysArray.some(d => d < 0);
          const hasExpiringSoon = daysArray.some(d => d >= 0 && d <= 30);

          if (expiringFilter === 'soon') {
            return hasExpiringSoon;
          } else if (expiringFilter === 'expired') {
            return hasExpired;
          } else if (expiringFilter === 'actual') {
            return !hasExpired && !hasExpiringSoon;
          }
          return true;
        });
      }

      setWorkers(workers);
    } catch (error) {
      if (error.response?.status === 401) {
        window.location.reload();
      } else {
        message.error('Ошибка загрузки данных');
      }
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, statusFilter, expiringFilter]);

  useEffect(() => {
    fetchWorkers();
  }, [fetchWorkers]);

  const handleAdd = () => {
    setEditingWorker(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (record) => {
    setEditingWorker(record);
    const { application_number, ...recordWithoutAppNum } = record;
    form.setFieldsValue({
      ...recordWithoutAppNum,
      birth_date: record.birth_date ? dayjs(record.birth_date) : null,
      payment_date: record.payment_date ? dayjs(record.payment_date) : null,
      registration_date: record.registration_date ? dayjs(record.registration_date) : null,
      insurance_date: record.insurance_date ? dayjs(record.insurance_date) : null,
      medical_policy_date: record.medical_policy_date ? dayjs(record.medical_policy_date) : null,
      patent_issue_date: record.patent_issue_date ? dayjs(record.patent_issue_date) : null,
      patent_expiry_date: record.patent_expiry_date ? dayjs(record.patent_expiry_date) : null,
      exam_date: record.exam_date ? dayjs(record.exam_date) : null,
    });
    setModalVisible(true);
  };

  const handleDelete = (id) => {
    Modal.confirm({
      title: 'Подтверждение удаления',
      content: 'Вы уверены, что хотите удалить этого сотрудника?',
      okText: 'Удалить',
      okType: 'danger',
      cancelText: 'Отмена',
      onOk: async () => {
        try {
          const token = localStorage.getItem('token');
          await axios.delete(`${API_URL}/${id}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          message.success('Сотрудник удален');
          fetchWorkers();
        } catch (error) {
          message.error('Ошибка удаления');
        }
      }
    });
  };

  const handleBulkDelete = async () => {
    if (selectedRowKeys.length === 0) return;
    
    Modal.confirm({
      title: 'Подтверждение удаления',
      content: `Вы уверены, что хотите удалить ${selectedRowKeys.length} сотрудников?`,
      okText: 'Удалить',
      okType: 'danger',
      cancelText: 'Отмена',
      onOk: async () => {
        try {
          const token = localStorage.getItem('token');
          await Promise.all(
            selectedRowKeys.map(id => 
              axios.delete(`${API_URL}/${id}`, {
                headers: { Authorization: `Bearer ${token}` }
              })
            )
          );
          message.success(`Удалено ${selectedRowKeys.length} сотрудников`);
          setSelectedRowKeys([]);
          fetchWorkers();
        } catch (error) {
          message.error('Ошибка массового удаления');
        }
      }
    });
  };

  const handleSubmit = async (values) => {
    try {
      const token = localStorage.getItem('token');
      
      // Функция для преобразования даты
      const toDate = (dateValue) => {
        if (!dateValue) return null;
        if (dateValue.toDate) return dateValue.toDate();
        if (dateValue instanceof Date) return dateValue;
        return null;
      };
      
      const data = {
        full_name: values.full_name,
        birth_date: toDate(values.birth_date),
        payment_date: toDate(values.payment_date),
        registration_date: toDate(values.registration_date),
        insurance_date: toDate(values.insurance_date),
        medical_policy_date: toDate(values.medical_policy_date),
        patent_profession: values.patent_profession || null,
        patent_expiry_date: toDate(values.patent_expiry_date),
        inn: values.inn || null,
        patent_issue_date: toDate(values.patent_issue_date),
        exam_date: toDate(values.exam_date),
        status: values.status || 'active',
      };

      if (editingWorker) {
        await axios.put(`${API_URL}/${editingWorker.id}`, data, {
          headers: { Authorization: `Bearer ${token}` }
        });
        message.success('Данные обновлены');
      } else {
        await axios.post(API_URL, data, {
          headers: { Authorization: `Bearer ${token}` }
        });
        message.success('Сотрудник добавлен');
      }
      setModalVisible(false);
      fetchWorkers();
    } catch (error) {
      message.error('Ошибка сохранения');
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'green';
      case 'expiring_soon': return 'orange';
      case 'expired': return 'red';
      case 'dismissed': return 'default';
      default: return 'default';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'active': return 'Активен';
      case 'expiring_soon': return 'Истекает';
      case 'expired': return 'Истек';
      case 'dismissed': return 'Уволен';
      default: return status;
    }
  };

  const getDaysRemaining = (expiryDate) => {
    if (!expiryDate) return null;
    const now = new Date();
    const expiry = new Date(expiryDate);
    const days = Math.ceil((expiry - now) / (1000 * 60 * 60 * 24));
    return days;
  };

  const renderDateWithStatus = (date) => {
    if (!date) return <div>-</div>;
    
    const days = getDaysRemaining(date);
    const formattedDate = dayjs(date).format('DD.MM.YYYY');
    
    if (days === null) return <div>{formattedDate}</div>;
    
    let color = 'green';
    let text = `${days} дн.`;
    
    if (days < 0) {
      color = 'red';
      text = `истекло`;
    } else if (days <= 30) {
      color = 'orange';
    }
    
    return (
      <div style={{ lineHeight: '1.4' }}>
        <div style={{ fontWeight: 500 }}>{formattedDate}</div>
        <div style={{ 
          color: color, 
          fontSize: '11px', 
          fontWeight: 'bold',
          marginTop: '2px'
        }}>
          ● {text}
        </div>
      </div>
    );
  };

  const columns = [
    {
      title: '№ заявки',
      dataIndex: 'application_number',
      key: 'application_number',
      width: 100,
      render: (text) => text || '-',
      sorter: (a, b) => (a.application_number || 0) - (b.application_number || 0),
    },
    {
      title: 'ФИО',
      dataIndex: 'full_name',
      key: 'full_name',
      sorter: (a, b) => a.full_name.localeCompare(b.full_name),
    },
    {
      title: 'Дата рождения',
      dataIndex: 'birth_date',
      key: 'birth_date',
      width: 120,
      render: (date) => date ? dayjs(date).format('DD.MM.YYYY') : '-',
      sorter: (a, b) => {
        if (!a.birth_date) return 1;
        if (!b.birth_date) return -1;
        return new Date(a.birth_date) - new Date(b.birth_date);
      },
    },
    {
      title: 'Дата оплаты',
      dataIndex: 'payment_date',
      key: 'payment_date',
      width: 110,
      render: renderDateWithStatus,
      sorter: (a, b) => {
        if (!a.payment_date) return 1;
        if (!b.payment_date) return -1;
        return new Date(a.payment_date) - new Date(b.payment_date);
      },
    },
    {
      title: 'Дата регистрации',
      dataIndex: 'registration_date',
      key: 'registration_date',
      width: 110,
      render: renderDateWithStatus,
      sorter: (a, b) => {
        if (!a.registration_date) return 1;
        if (!b.registration_date) return -1;
        return new Date(a.registration_date) - new Date(b.registration_date);
      },
    },
    {
      title: 'Дата мед. страховки',
      dataIndex: 'insurance_date',
      key: 'insurance_date',
      width: 110,
      render: renderDateWithStatus,
      sorter: (a, b) => {
        if (!a.insurance_date) return 1;
        if (!b.insurance_date) return -1;
        return new Date(a.insurance_date) - new Date(b.insurance_date);
      },
    },
    {
      title: 'Дата мед. полиса',
      dataIndex: 'medical_policy_date',
      key: 'medical_policy_date',
      width: 110,
      render: renderDateWithStatus,
      sorter: (a, b) => {
        if (!a.medical_policy_date) return 1;
        if (!b.medical_policy_date) return -1;
        return new Date(a.medical_policy_date) - new Date(b.medical_policy_date);
      },
    },
    {
      title: 'Срок патента',
      dataIndex: 'patent_expiry_date',
      key: 'patent_expiry',
      width: 110,
      render: renderDateWithStatus,
      sorter: (a, b) => {
        if (!a.patent_expiry_date) return 1;
        if (!b.patent_expiry_date) return -1;
        return new Date(a.patent_expiry_date) - new Date(b.patent_expiry_date);
      },
    },
    {
      title: 'Дата экзамена',
      dataIndex: 'exam_date',
      key: 'exam_date',
      width: 110,
      render: renderDateWithStatus,
      sorter: (a, b) => {
        if (!a.exam_date) return 1;
        if (!b.exam_date) return -1;
        return new Date(a.exam_date) - new Date(b.exam_date);
      },
    },
    {
      title: 'Профессия',
      dataIndex: 'patent_profession',
      key: 'patent_profession',
      width: 120,
      render: (text) => text || '-',
    },
    {
      title: 'ИНН',
      dataIndex: 'inn',
      key: 'inn',
      width: 120,
      render: (text) => text || '-',
    },
    {
      title: 'Статус',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status) => (
        <Tag color={getStatusColor(status)}>{getStatusText(status)}</Tag>
      ),
      sorter: (a, b) => a.status.localeCompare(b.status),
    },
    {
      title: 'Действия',
      key: 'action',
      render: (_, record) => (
        <Space size="small" className="no-print">
          <Button type="link" icon={<EditOutlined />} onClick={() => handleEdit(record)} />
          <Button type="link" danger icon={<DeleteOutlined />} onClick={() => handleDelete(record.id)} />
        </Space>
      ),
    },
  ];

  return (
    <div className="app">
      <div className="print-header" style={{ display: 'none' }}>
        <h1>Список трудовых мигрантов</h1>
      </div>
      
      <Card title="Список сотрудников">
        <Row gutter={[16, 16]} className="filters-row no-print" style={{ marginBottom: 16 }}>
          <Col xs={24} sm={12} md={6}>
            <Input
              placeholder="Поиск по ФИО"
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              allowClear
            />
          </Col>
          <Col xs={24} sm={12} md={4}>
            <Select
              placeholder="Статус"
              value={statusFilter}
              onChange={setStatusFilter}
              allowClear
              style={{ width: '100%' }}
            >
              <Option value="active">Активен</Option>
              <Option value="expiring_soon">Истекает</Option>
              <Option value="expired">Истек</Option>
              <Option value="dismissed">Уволен</Option>
            </Select>
          </Col>
          <Col xs={24} sm={12} md={4}>
            <Select
              placeholder="Фильтр по документам"
              value={expiringFilter}
              onChange={setExpiringFilter}
              allowClear
              style={{ width: '100%' }}
            >
              <Option value="soon">Скоро истечет</Option>
              <Option value="expired">Истек</Option>
              <Option value="actual">Актуально</Option>
            </Select>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Space>
              <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
                Добавить
              </Button>
              <Button icon={<PrinterOutlined />} onClick={handlePrint}>
                Печать
              </Button>
            </Space>
          </Col>
        </Row>

        {selectedRowKeys.length > 0 && (
          <div style={{ marginBottom: 16, padding: '8px 16px', background: '#f6ffed', border: '1px solid #b7eb8f', borderRadius: 4 }}>
            <Space>
              <span>Выбрано: <strong>{selectedRowKeys.length}</strong></span>
              <Button type="primary" danger size="small" onClick={handleBulkDelete}>
                Удалить выбранных
              </Button>
              <Button size="small" onClick={() => setSelectedRowKeys([])}>
                Снять выделение
              </Button>
            </Space>
          </div>
        )}

        <Table
          columns={columns}
          dataSource={workers}
          loading={loading}
          rowKey="id"
          rowSelection={{
            selectedRowKeys,
            onChange: setSelectedRowKeys,
          }}
          rowClassName={(record) => {
            if (record.status === 'dismissed') return 'row-dismissed';
            if (record.status === 'expiring_soon') return 'row-expiring';
            if (record.status === 'expired') return 'row-expired';
            return '';
          }}
          pagination={{
            pageSize: pageSize,
            showSizeChanger: true,
            pageSizeOptions: ['30', '50', '100', '200'],
            showTotal: (total, range) => `${range[0]}-${range[1]} из ${total} записей`,
            onShowSizeChange: (current, size) => setPageSize(size)
          }}
          scroll={{ x: 'max-content', y: 'calc(100vh - 350px)' }}
          sticky
          bordered
          className="workers-table"
          size="small"
        />
      </Card>

      <Modal
        title={editingWorker ? 'Редактирование' : 'Добавление сотрудника'}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        width={700}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                name="full_name"
                label="ФИО"
                rules={[{ required: true, message: 'Введите ФИО' }]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="birth_date" label="Дата рождения">
                <MaskedDateField placeholder="ДД.ММ.ГГГГ" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="payment_date" label="Дата оплаты">
                <MaskedDateField placeholder="ДД.ММ.ГГГГ" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="registration_date" label="Дата регистрации">
                <MaskedDateField placeholder="ДД.ММ.ГГГГ" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="insurance_date" label="Дата мед. страховки">
                <MaskedDateField placeholder="ДД.ММ.ГГГГ" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="medical_policy_date" label="Дата мед. полиса">
                <MaskedDateField placeholder="ДД.ММ.ГГГГ" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="status" label="Статус" initialValue="active">
                <Select>
                  <Option value="active">Активен</Option>
                  <Option value="expiring_soon">Истекает</Option>
                  <Option value="expired">Истек</Option>
                  <Option value="dismissed">Уволен</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>
          
          <Divider>Патент</Divider>
          
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="patent_profession" label="Профессия">
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="patent_expiry_date" label="Срок действия патента">
                <MaskedDateField placeholder="ДД.ММ.ГГГГ" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="inn" label="ИНН">
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="patent_issue_date" label="Дата выдачи патента">
                <MaskedDateField placeholder="ДД.ММ.ГГГГ" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="exam_date" label="Дата экзамена">
                <MaskedDateField placeholder="ДД.ММ.ГГГГ" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item>
            <Space style={{ float: 'right' }}>
              <Button onClick={() => setModalVisible(false)}>Отмена</Button>
              <Button type="primary" htmlType="submit">
                {editingWorker ? 'Обновить' : 'Добавить'}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

export default Workers;
